import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import FooterSection from "../../components/pages/home/footer-section";
import NavbarSection from "../../components/pages/home/navbar-section";
import ExternalArrow from "../../components/common/external-arrow";
import PageMeta from "../../components/common/page-meta";
import ForwardArrow from "../../components/common/forward-arrow";
import {
  getPublishedThoughts,
  subscribeToPublishedThoughts,
} from "../../data/firebase";
import mediumThoughts from "../../data/thoughts/medium";
import { ThoughtPost } from "../../data/thoughts/props";
import { isInstagramMobileWebView } from "../../utils/instagram-browser";
import { refreshPage } from "../../utils/refresh-page";
import {
  ThoughtArrow,
  ThoughtDescription,
  ThoughtLink,
  ThoughtList,
  ThoughtSource,
  ThoughtsAside,
  ThoughtsIntroduction,
  ThoughtsLoading,
  ThoughtsNote,
  ThoughtsPage,
  ThoughtsPaper,
  ThoughtsRefresh,
  ThoughtsTitle,
  ThoughtTitle,
} from "./style";

const formatDate = (value: string) => {
  if (!value) {
    return "New thought";
  }
  return new Intl.DateTimeFormat("en", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(new Date(value));
};

interface MediumFeedItem {
  title: string;
  url: string;
  date: string;
}

const mergeMediumThoughts = (feedItems: MediumFeedItem[]) => {
  const savedByUrl = new Map(
    mediumThoughts.map((thought) => [thought.url, thought]),
  );

  const synced = feedItems.map((item) => {
    const saved = savedByUrl.get(item.url);
    return {
      title: saved?.title || item.title,
      url: item.url,
      date: formatDate(item.date),
      description: saved?.description || "A new note from my Medium journal.",
    };
  });

  const syncedUrls = new Set(synced.map((thought) => thought.url));

  return [
    ...synced,
    ...mediumThoughts.filter((thought) => !syncedUrls.has(thought.url)),
  ];
};

const getSortTime = (value: string) => {
  const time = Date.parse(value);
  return Number.isNaN(time) ? Number.MAX_SAFE_INTEGER : time;
};

const Thoughts = () => {
  const [thoughts, setThoughts] = useState<ThoughtPost[]>([]);
  const [syncedMediumThoughts, setSyncedMediumThoughts] =
    useState(mediumThoughts);
  const [loading, setLoading] = useState(true);
  const showInstagramHelp = isInstagramMobileWebView();
  const orderedThoughts = [
    ...thoughts.map((thought) => ({
      kind: "site" as const,
      thought,
      sortTime: getSortTime(thought.publishedAt),
    })),
    ...syncedMediumThoughts.map((thought) => ({
      kind: "medium" as const,
      thought,
      sortTime: getSortTime(thought.date),
    })),
    ].sort((a, b) => b.sortTime - a.sortTime);

  useEffect(() => {
    let isActive = true;
    let hasReceivedLiveUpdate = false;

    getPublishedThoughts()
      .then((posts) => {
        if (isActive && !hasReceivedLiveUpdate) {
          setThoughts(posts);
          setLoading(false);
        }
      })
      .catch(() => {
        if (isActive && !hasReceivedLiveUpdate) {
          setLoading(false);
        }
      });

    const unsubscribe = subscribeToPublishedThoughts(
      (posts) => {
        if (isActive) {
          hasReceivedLiveUpdate = true;
          setThoughts(posts);
          setLoading(false);
        }
      },
      () => {
        // The initial request above still keeps published writing available.
      }
    );

    fetch("/.netlify/functions/medium-feed")
      .then((response) => {
        if (
          !response.ok ||
          !response.headers.get("content-type")?.includes("application/json")
        ) {
          throw new Error("Medium feed is unavailable");
        }
        return response.json();
      })
      .then((data: { items?: MediumFeedItem[] }) => {
        if (data.items?.length) {
          setSyncedMediumThoughts(mergeMediumThoughts(data.items));
        }
      })
      .catch(() => {
        // Keep the curated entries visible while offline or on the local CRA server.
      });

    return () => {
      isActive = false;
      unsubscribe();
    };
  }, []);

  return (
    <ThoughtsPage>
      <PageMeta
        title="Thoughts — Freddy's"
        description="Personal notes about the things Freddy notices, questions, and wants to remember."
      />
      <NavbarSection percentage={0} showLine={false} />
      <ThoughtsPaper>
        <ThoughtsAside>
          <ThoughtsTitle>Notes from Freddy.</ThoughtsTitle>
          <ThoughtsIntroduction>
            Personal notes about the things I notice, question, and want to
            remember. Older pieces open on Medium; new ones are written and read
            here.
          </ThoughtsIntroduction>
          {showInstagramHelp && (
            <ThoughtsNote>
              Instagram may load slowly. For a better experience, open this
              page in an external browser. {" "}
              <ThoughtsRefresh type="button" onClick={refreshPage}>
                Refresh
              </ThoughtsRefresh>{" "}
              if needed.
            </ThoughtsNote>
          )}
        </ThoughtsAside>
        <ThoughtList>
          {orderedThoughts.map((entry) =>
            entry.kind === "site" ? (
              <ThoughtLink
                as={Link}
                key={entry.thought.slug}
                to={`/thoughts/${entry.thought.slug}`}
              >
                <div>
                  <ThoughtSource>{formatDate(entry.thought.publishedAt)}</ThoughtSource>
                  <ThoughtTitle>{entry.thought.title}</ThoughtTitle>
                  <ThoughtDescription>{entry.thought.description}</ThoughtDescription>
                </div>
                <ThoughtArrow>
                  <ForwardArrow />
                </ThoughtArrow>
              </ThoughtLink>
            ) : (
              <ThoughtLink
                href={entry.thought.url}
                key={entry.thought.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                <div>
                  <ThoughtSource>Medium · {entry.thought.date}</ThoughtSource>
                  <ThoughtTitle>{entry.thought.title}</ThoughtTitle>
                  <ThoughtDescription>{entry.thought.description}</ThoughtDescription>
                </div>
                <ThoughtArrow>
                  <ExternalArrow />
                </ThoughtArrow>
              </ThoughtLink>
            ),
          )}
          {loading && <ThoughtsLoading>Loading…</ThoughtsLoading>}
        </ThoughtList>
      </ThoughtsPaper>
      <FooterSection />
    </ThoughtsPage>
  );
};

export default Thoughts;
